import { useEffect, useState } from 'react';
import { SliceState } from 'store';
import { Inventory } from 'types';

/**
 * Number of inventory items to display per page
 */
const ITEMS_PER_PAGE = 8;

/**
 * Initial page number to display when pagination is initialized
 */
const INITIAL_PAGE = 1;

/**
 * Custom hook for handling pagination of restricted inventory items
 *
 * This hook manages the complex logic of filtering inventory items based on
 * a list of restricted inventory IDs, and then paginating the results.
 *
 * @param inventory - The complete inventory slice from Redux store
 * @param restrictedInventory - The slice containing IDs of restricted inventory items
 * @returns Pagination state and handlers for the restricted inventory items
 */
export default function useRestrictedInventoryPagination(
	inventory: SliceState<Inventory>,
	restrictedInventory: SliceState<number>
) {
	// Track whether both inventory and restricted IDs have been fetched
	const [isDataFetched, setIsDataFetched] = useState(false);
	// Counter to track when both data sources have been loaded
	const [fetchedCount, setFetchedCount] = useState(0);
	// Cache of the full inventory list
	const [fetchedInventory, setFetchedInventory] = useState<Inventory[]>([]);
	// Cache of the restricted inventory IDs
	const [fetchedIds, setFetchedIds] = useState<number[]>([]);
	// Filtered data (intersection of inventory and restricted IDs)
	const [data, setData] = useState<Inventory[]>([]);
	// Whether to show pagination controls
	const [showPagination, setShowPagination] = useState(false);
	// Total number of pages based on data length and items per page
	const [totalPages, setTotalPages] = useState(0);
	// Current active page number
	const [currentPage, setCurrentPage] = useState(0);
	// Array of page numbers to display in pagination controls
	const [pages, setPages] = useState<number[]>([]);
	// Subset of data for the current page
	const [paginatedData, setPaginatedData] = useState<Inventory[]>([]);

	/**
	 * Update local inventory cache when inventory data changes
	 * Only updates if there's new data and it's different from what we have
	 */
	useEffect(() => {
		if (inventory.list.length === 0 || inventory.list.length === data.length) {
			return;
		} else {
			setFetchedInventory(inventory.list);
			setFetchedCount((prev) => prev + 1);
		}
	}, [inventory.updatedAt]);

	/**
	 * Update restricted IDs cache when restricted inventory data changes
	 */
	useEffect(() => {
		setFetchedIds(restrictedInventory.list);
		setFetchedCount((prev) => prev + 1);
	}, [restrictedInventory.updatedAt]);

	/**
	 * Mark data as fetched once both inventory and restricted IDs are loaded
	 * Uses fetchedCount to track when both data sources have been loaded
	 */
	useEffect(() => {
		if (fetchedCount < 2) return;
		setIsDataFetched(true);
	}, [fetchedCount]);

	/**
	 * Filter inventory items to only include those with IDs in the restricted list
	 * This creates the intersection of all inventory and restricted inventory IDs
	 */
	useEffect(() => {
		const filtered = fetchedInventory.filter((element) => fetchedIds.includes(element.id));
		setData(filtered);
	}, [fetchedIds]);

	/**
	 * Calculate total number of pages based on data length
	 */
	useEffect(() => {
		if (data.length === 0) {
			setTotalPages(0);
			return;
		}

		const TOTAL_PAGES = Math.ceil(data.length / ITEMS_PER_PAGE);
		setTotalPages(TOTAL_PAGES);
	}, [data]);

	/**
	 * Initialize current page and determine if pagination controls should be shown
	 * Only show pagination if there's more than one page of data
	 */
	useEffect(() => {
		if (totalPages === 0) {
			setCurrentPage(1);
			setShowPagination(false);
			return;
		}

		const CURRENT = INITIAL_PAGE;
		setCurrentPage(CURRENT);
		setShowPagination(data.length > ITEMS_PER_PAGE);
	}, [totalPages, data]);

	/**
	 * Calculate which page numbers to display and which data items to show
	 * Handles complex pagination logic including:
	 * - Showing a window of 5 page numbers
	 * - Adjusting the window based on current page position
	 * - Slicing the data to show only items for the current page
	 */
	useEffect(() => {
		if (currentPage === 0) return;

		// Maximum number of page buttons to show (5 or fewer)
		const PAGES_SHOWN = totalPages <= 5 ? totalPages : 5;
		let START_PAGE: number, END_PAGE: number;

		// Calculate which page numbers to show based on current page position
		if (currentPage <= Math.floor(PAGES_SHOWN / 2) + 1) {
			// Near the beginning - show first 5 pages
			START_PAGE = 1;
			END_PAGE = PAGES_SHOWN;
		} else if (currentPage + Math.floor(PAGES_SHOWN / 2) - 1 >= totalPages) {
			// Near the end - show last 5 pages
			START_PAGE = totalPages - (PAGES_SHOWN - 1);
			END_PAGE = totalPages;
		} else {
			// In the middle - show current page and 2 pages on each side
			START_PAGE = currentPage - Math.floor(PAGES_SHOWN / 2);
			END_PAGE = currentPage + (Math.floor(PAGES_SHOWN / 2) - 1);
		}

		// Calculate data slice indices for current page
		const START_INDEX = (currentPage - 1) * ITEMS_PER_PAGE;
		const END_INDEX = Math.min(START_INDEX + (ITEMS_PER_PAGE - 1), data.length - 1);

		// Generate array of page numbers to display
		const PAGES = new Array(END_PAGE + 1 - START_PAGE).fill('').map((_, index) => START_PAGE + index);

		// Get data slice for current page
		const PAGINATED_DATA = data.slice(START_INDEX, END_INDEX + 1);

		setPages(PAGES);
		setPaginatedData(PAGINATED_DATA);
	}, [currentPage, data, totalPages]);

	/**
	 * Set the current page to a specific page number
	 * Validates that the page is within the valid range
	 */
	const setPage = (page: number) => {
		if (page < 1 || page > totalPages) {
			return;
		}
		setCurrentPage(page);
	};

	/**
	 * Increment the current page by 1, unless already at the last page
	 */
	const incrementPage = () =>
		setCurrentPage((prev) => {
			if (prev === totalPages) {
				return prev;
			}
			return prev + 1;
		});

	/**
	 * Decrement the current page by 1, unless already at the first page
	 */
	const decrementPage = () =>
		setCurrentPage((prev) => {
			if (prev === 1) {
				return prev;
			}
			return prev - 1;
		});

	/**
	 * Return all necessary state and handlers for pagination
	 */
	return {
		isDataFetched,
		listing: inventory.list,
		restricted: restrictedInventory.list,
		setData,
		paginatedData,
		setPaginatedData,
		paginationProps: {
			showPagination,
			pages,
			currentPage,
			totalPages,
			setCurrentPage,
			setPage,
			incrementPage,
			decrementPage,
		},
	};
}
